import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageTitle from "../elements/PageTitle";
import NewsLetter from "../elements/NewsLetter";
import "../assets/css/PackagesDetails.css"; 

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const API_URL = `${API_BASE}/courses/categories`;

const CategoriesDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPacks() {
      try {
        const res = await fetch(`${API_URL}/${id}/packs`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) throw new Error("Failed to fetch pack details");

        const data = await res.json();
        setPacks(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchPacks();
  }, [id]);

  const handlePackClick = async (packId) => {
    const token = localStorage.getItem('token');
    
    // Check if user is logged in
    if (!token) {
      // Show message to user
      // Store the intended destination for redirect after login
      localStorage.setItem("redirectAfterLogin", `/Categories/Category/${id}/Pack/${packId}/Courses`);
      navigate("/login");
      return;
    }

    try {
      // Check subscription status
      const res = await fetch(`${API_BASE}/courses/packs/${packId}/subscription-status`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 401) {
        // Token expired or invalid, redirect to login
        localStorage.setItem("redirectAfterLogin", `/Categories/Category/${id}/Pack/${packId}/Courses`);
        navigate("/login");
        return;
      }

      if (res.status === 403) {
        const errorData = await res.json().catch(() => ({}));
        // Redirect to pricing page to renew subscription
        navigate(`/Categories/Category/${id}/Pack/${packId}`);
        return;
      }

      if (!res.ok) {
        // If endpoint fails, redirect to pricing page
        navigate(`/Categories/Category/${id}/Pack/${packId}`);
        return;
      }

      const data = await res.json();
      
      // If user has active subscription, redirect to courses page
      if (data.hasActiveSubscription) {
        navigate(`/Categories/Category/${id}/Pack/${packId}/Courses`);
      } 
      // If no active subscription, redirect to pricing page
      else {
        navigate(`/Categories/Category/${id}/Pack/${packId}`);
      }
    } catch (err) {
      console.error("Error checking subscription status:", err);
      // On error, redirect to pricing page
      navigate(`/Categories/Category/${id}/Pack/${packId}`);
    }
  };

  return (
    <div className="page-wraper">
      <div className="page-content bg-white">
        <PageTitle activePage="Package Details" parentTitle="Packages" />

        <div className="container py-5">
          {error && <p className="text-danger">{error}</p>}
          {loading && !error && (
            <div
              className="boxes"
              style={{ margin: "40px auto", width: "96px", height: "64px" }}
            >
              <div className="box">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              <div className="box">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              <div className="box">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
              <div className="box">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          )}

          {!loading && !error && packs.length > 0 ? (
            <div className="d-flex flex-wrap justify-content-center gap-4">
              {packs.map((pack, index) => (
                <div
                  key={`${pack.id}-${index}`}
                  onClick={() => handlePackClick(pack.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="card position-relative">
                    <div className="bg"></div>
                    <div className="blob"></div>
                    <div
                      className="card-content"
                      style={{
                        position: "relative",
                        zIndex: 3,
                        textAlign: "center",
                        padding: "20px",
                        color: "#000000ff",
                      }}
                    >
                      {pack.promotion && (
                        <div className="badge bg-danger mb-2" style={{ fontSize: "0.85rem" }}>
                          🎉 New Promotion !
                        </div>
                      )}
                      <h4 className="fw-bold mb-2">{pack.name}</h4>
                      <em className="text-muted small d-block mb-2">
  {pack.description}
</em>

<strong className="fw-bold d-block mb-3">
  Category: {pack.category_name}
</strong>


                      {pack.plans && pack.plans.length > 0 && (
                        <div className="plans">
                          {pack.plans.map((plan, idx) => {
                            // Use backend field names: price_cents_original and price_cents_final
                            const originalPrice = plan.price_cents_original || 0;
                            const discountedPrice = plan.price_cents_final || plan.price_cents_original || 0;
                            const discountAmount = originalPrice - discountedPrice;
                            const hasDiscount = pack.promotion && discountAmount > 0 && originalPrice > 0;

                            return (
                              <div key={`${plan._id || idx}-${idx}`} className="plan-item mb-2">
                                <strong>{plan.label}</strong> –{" "}
                                {plan.duration_days} days –{" "}
                                {hasDiscount ? (
                                  <span>
                                    <span
                                      className="text-decoration-line-through text-muted me-2"
                                      style={{ fontSize: "0.9rem" }}
                                    >
                                      ${(originalPrice / 100).toFixed(2)}
                                    </span>
                                    <span className="text-success fw-bold">
                                      ${(discountedPrice / 100).toFixed(2)}
                                    </span>
                                    <span className="text-danger ms-2" style={{ fontSize: "0.85rem" }}>
                                      (Save ${(discountAmount / 100).toFixed(2)})
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-success">
                                    ${((discountedPrice || originalPrice || 0) / 100).toFixed(2)}
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !loading && !error && <p>No packs found for this category.</p>
          )}
        </div>

        <section className="call-action style-1 footer-action">
          <div className="container">
            <NewsLetter />
          </div>
        </section>
      </div>
    </div>
  );
};

export default CategoriesDetails;